// src/services/mysql.js — Frontend MySQL/MariaDB service
import axios from 'axios';
import { API_URL as BASE_API_URL } from '../config.js';

class MySQLService {
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * Execute a SQL query against MySQL. Returns array of row objects.
   * Response is normalized server-side to {columns, dataset} — same as QuestDB.
   */
  async query(sql, database, _retries = 2) {
    try {
      const response = await axios.get(`${BASE_API_URL}/mysql/query`, {
        params: { sql, database },
        headers: this.getAuthHeaders()
      });
      return this.formatData(response.data);
    } catch (error) {
      if (error.response?.status === 503 && _retries > 0) {
        await new Promise(r => setTimeout(r, 1000));
        return this.query(sql, database, _retries - 1);
      }
      console.error('MySQL query error:', error);
      throw error;
    }
  }

  /** Get available databases */
  async getDatabases() {
    try {
      const response = await axios.get(`${BASE_API_URL}/mysql/databases`, {
        headers: this.getAuthHeaders()
      });
      return this.formatData(response.data);
    } catch (error) {
      console.error('MySQL databases error:', error);
      throw error;
    }
  }

  /** Get tables in a database */
  async getTables(database) {
    try {
      const response = await axios.get(`${BASE_API_URL}/mysql/tables`, {
        params: { database },
        headers: this.getAuthHeaders()
      });
      return this.formatData(response.data);
    } catch (error) {
      console.error('MySQL tables error:', error);
      throw error;
    }
  }

  /**
   * Format {columns, dataset} response to array of objects.
   * Same logic as questdbService.formatData — kept here to avoid circular deps.
   */
  formatData(response) {
    if (!response || !response.dataset || !response.columns) return [];

    const { columns, dataset } = response;
    if (dataset.length === 0) return [];

    const numericTypes = ['DOUBLE', 'REAL', 'INTEGER', 'BIGINT', 'SMALLINT', 'FLOAT', 'NUMERIC', 'DECIMAL'];

    return dataset.map(row => {
      const obj = {};
      columns.forEach((col, i) => {
        let value = Array.isArray(row) ? row[i] : row[col.name];

        if (col.type === 'TIMESTAMP' && value) {
          const d = new Date(value);
          if (!isNaN(d.getTime())) value = d.toISOString();
        }

        if (numericTypes.includes(col.type?.toUpperCase())) {
          if (typeof value === 'string' && value.trim() !== '') {
            const num = Number(value);
            value = isNaN(num) ? 0 : num;
          } else if (value === null || value === undefined || value === '') {
            value = 0;
          }
        }

        obj[col.name] = value;
      });
      return obj;
    });
  }
}

export default new MySQLService();
